import { useState } from 'react';
import { Btn } from '../../components/ui/Btn.jsx';
import { ouvreDonnees, ouvrePiece } from '../../components/DocumentsJoints.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import { DEVISE, fmt } from '../../lib/currency';
import { contenuDuDossier, demandeDuDossier } from '../../lib/dossier-annee';
import { tr, trf } from '../../lib/i18n';
import { libelleMoyen } from '../../lib/paye-le';
import { tvaRecuperable } from '../../lib/vat';
import { C } from '../../theme/palette';
import { ActionsDossier } from './EnvoiDepenses.jsx';
import { anneeLaPlusRecente, ChoixDesMois, moisDeLAnnee, ResumeDesMois } from './ChoixDesMois.jsx';
import {
  CAFE_ET_REPAS,
  dansLaPeriode,
  dateIso,
  libellePeriode,
  moisLisible,
} from './TicketsA4.jsx';

/**
 * Dans la Comptabilité, pour chaque type de dépense (café, repas,
 * sous-traitance, matériel…), deux choses différentes :
 *
 * <ul>
 *   <li>« 👁 Consulter » : regarder ses pièces à l'écran, par période, avec le
 *       total, et chaque ticket ou facture en grand, l'un après l'autre ;</li>
 *   <li>« 🖨 Imprimer / Envoyer » : un PDF (la liste, puis chaque ticket et
 *       chaque facture telle que reçue) à imprimer, enregistrer, partager ou
 *       envoyer par e-mail.</li>
 * </ul>
 *
 * <p>« Il faut mettre deux trucs : un où je peux consulter toutes mes
 * dépenses sans vouloir imprimer, et un où je veux imprimer. » Les deux
 * boutons « Tickets sur A4 » d'avant faisaient la même chose.
 */

const TYPES_CAFE_ET_REPAS = ['cafe', 'repas'];

/** Le justificatif d'une pièce se montre-t-il comme une image ? */
const imageDe = (it) => {
  const src = it?.piece || it?.photo;
  if (!src) return null;
  return String(src).startsWith('data:image') || String(it.pieceMime || '').startsWith('image/')
    ? src
    : null;
};

/** La pièce a-t-elle un justificatif à ouvrir ? */
const aUnJustificatif = (it) => !!(it?.pieceJointe || it?.piece || it?.photo);

/** Ouvre le justificatif tel qu'il a été reçu (photo ou PDF). */
const ouvreJustificatif = (it) => {
  if (it.pieceJointe) ouvrePiece(it.pieceJointe);
  else ouvreDonnees(it.piece || it.photo, it.libelle || it.fournisseur);
};

/** Le nom à montrer : le fournisseur, sinon le libellé. */
const nomDe = (it) => it.fournisseur || it.libelle || tr('Dépense');

/** Espèces, carte, banque : comment la pièce a été payée. */
const payePar = (it) => libelleMoyen(it.payePar || it.paiement || '');

/** La TVA récupérable d'une pièce : calculée pour une dépense, lue pour une facture. */
const tvaDe = (it, soc) =>
  it.origine === 'frais' ? tvaRecuperable([it], FRAIS_CAT, soc) : +it.montantTva || 0;

const styleChamp = {
  width: '100%',
  border: '1px solid ' + C.border,
  borderRadius: 9,
  padding: '10px',
  fontSize: 13,
  background: '#fff',
  boxSizing: 'border-box',
};
const styleEtiquette = {
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  display: 'block',
  marginBottom: 4,
};
const styleCadre = {
  background: '#fff',
  border: '2px solid ' + C.navy,
  borderRadius: 12,
  padding: 12,
  marginBottom: 10,
};

/**
 * Une pièce en grand, plein écran, avec ‹ › pour passer à la précédente ou à
 * la suivante, comme un album photo.
 */
function Album({ pieces, rang, onRang, onFermer }) {
  const it = pieces[rang];
  if (!it) return null;
  const image = imageDe(it);
  const fleche = (sens, texte, nom) => (
    <button
      type="button"
      aria-label={nom}
      disabled={rang + sens < 0 || rang + sens >= pieces.length}
      onClick={() => onRang(rang + sens)}
      style={{
        minWidth: 44,
        minHeight: 64,
        border: 'none',
        borderRadius: 12,
        background: '#1E293B',
        color: '#fff',
        fontSize: 28,
        fontWeight: 800,
        cursor: 'pointer',
        opacity: rang + sens < 0 || rang + sens >= pieces.length ? 0.3 : 1,
      }}
    >
      {texte}
    </button>
  );
  return (
    <div
      role="dialog"
      aria-label={nomDe(it)}
      data-testid="album"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: '#0F172A',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 12,
        }}
      >
        <button
          type="button"
          onClick={onFermer}
          style={{
            border: 'none',
            borderRadius: 10,
            background: '#1E293B',
            color: '#fff',
            fontSize: 14,
            fontWeight: 800,
            padding: '10px 12px',
            cursor: 'pointer',
          }}
        >
          ✕ {tr('Fermer')}
        </button>
        <span style={{ fontSize: 13, color: '#CBD5E1' }}>
          {rang + 1} / {pieces.length}
        </span>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 8px',
        }}
      >
        {fleche(-1, '‹', tr('Pièce précédente'))}
        <div
          style={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {image ? (
            <button
              type="button"
              aria-label={tr('Voir le justificatif')}
              onClick={() => ouvreJustificatif(it)}
              style={{ border: 'none', background: 'none', padding: 0, cursor: 'zoom-in' }}
            >
              <img
                src={image}
                alt=""
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 10 }}
              />
            </button>
          ) : aUnJustificatif(it) ? (
            <Btn
              label={'📄 ' + tr('Ouvrir la facture')}
              color={C.teal}
              onClick={() => ouvreJustificatif(it)}
            />
          ) : (
            <span style={{ color: '#CBD5E1', fontSize: 13 }}>{tr('Sans justificatif')}</span>
          )}
        </div>
        {fleche(1, '›', tr('Pièce suivante'))}
      </div>
      <div style={{ padding: '14px 18px 22px' }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>
          {nomDe(it)} · {DEVISE} {fmt(+it.montant || 0)}
        </div>
        <div style={{ fontSize: 12.5, color: '#CBD5E1', marginTop: 4 }}>
          {[it.date, payePar(it), it.ou].filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  );
}

/**
 * « 👁 Voir » : les pièces des mois choisis, le total en tête, rangées par
 * mois ; toucher une pièce l'ouvre en grand.
 *
 * @param {object} props propriétés
 * @param {object[]} props.items pièces du type (dépenses et factures de fournisseurs)
 * @param {string[]} props.mois mois choisis, « AAAA-MM »
 * @param {object} [props.soc] entreprise, pour la TVA
 * @returns {import('react').ReactElement} la liste
 */
export function ConsulterDuType({ items, mois, soc }) {
  const [rang, setRang] = useState(null);
  const pieces = (items || [])
    .filter((it) => dansLaPeriode(dateIso(it.date), mois))
    .sort((a, b) => dateIso(b.date).localeCompare(dateIso(a.date)));
  const total = pieces.reduce((s, it) => s + (+it.montant || 0), 0);
  const tva = pieces.reduce((s, it) => s + tvaDe(it, soc), 0);
  const parMois = [];
  pieces.forEach((it, i) => {
    const m = dateIso(it.date).slice(0, 7);
    const dernier = parMois[parMois.length - 1];
    if (dernier && dernier.mois === m) dernier.lignes.push({ it, i });
    else parMois.push({ mois: m, lignes: [{ it, i }] });
  });

  return (
    <div data-testid="consulter">
      <div
        style={{
          background: C.navy,
          color: '#fff',
          borderRadius: 12,
          padding: '10px 12px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 6,
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 9.5, color: '#C9D0DC' }}>{tr('Pièces')}</div>
          <b style={{ fontSize: 15 }}>{pieces.length}</b>
        </div>
        <div>
          <div style={{ fontSize: 9.5, color: '#C9D0DC' }}>{tr('Total')}</div>
          <b style={{ fontSize: 15 }}>
            {DEVISE} {fmt(total)}
          </b>
        </div>
        <div>
          <div style={{ fontSize: 9.5, color: '#C9D0DC' }}>{tr('dont TVA')}</div>
          <b style={{ fontSize: 15 }}>{fmt(tva)}</b>
        </div>
      </div>
      {pieces.length === 0 && (
        <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', padding: 10 }}>
          {tr('Aucune pièce pour cette période.')}
        </div>
      )}
      {parMois.map(({ mois: m, lignes }) => (
        <div key={m || 'sans-date'} style={{ marginBottom: 8 }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              color: C.grey,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              margin: '6px 2px',
            }}
          >
            {m ? moisLisible(m) : tr('Sans date')} · {DEVISE}{' '}
            {fmt(lignes.reduce((s, { it }) => s + (+it.montant || 0), 0))}
          </div>
          {lignes.map(({ it, i }) => {
            const image = imageDe(it);
            return (
              <button
                key={i}
                type="button"
                data-testid="piece-consultee"
                onClick={() => setRang(i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  textAlign: 'left',
                  background: '#fff',
                  border: '1px solid ' + C.border,
                  borderRadius: 10,
                  padding: 8,
                  marginBottom: 6,
                  cursor: 'pointer',
                  color: C.ink,
                }}
              >
                <span
                  style={{
                    width: 44,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: 7,
                    overflow: 'hidden',
                    background: C.navyXL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                  }}
                >
                  {image ? (
                    <img
                      src={image}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : aUnJustificatif(it) ? (
                    '📄'
                  ) : (
                    '—'
                  )}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ display: 'block', fontSize: 12, overflowWrap: 'anywhere' }}>
                    {nomDe(it)}
                  </b>
                  <span style={{ fontSize: 10.5, color: C.grey }}>
                    {[it.date, payePar(it), it.ou].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <b style={{ fontSize: 12.5, flexShrink: 0 }}>{fmt(+it.montant || 0)}</b>
              </button>
            );
          })}
        </div>
      ))}
      {pieces.length > 0 && (
        <div style={{ fontSize: 10, color: C.muted }}>
          {tr('Touche une pièce pour la voir en grand.')}
        </div>
      )}
      {rang !== null && (
        <Album pieces={pieces} rang={rang} onRang={setRang} onFermer={() => setRang(null)} />
      )}
    </div>
  );
}

/**
 * « 📤 Envoyer » : les pièces des mois choisis en un PDF — la liste, puis
 * chaque ticket (plusieurs par feuille) et chaque facture telle que reçue —,
 * par e-mail à qui on veut, par WhatsApp, ou à imprimer.
 *
 * @param {object} props propriétés
 * @param {string} props.type type de dépense
 * @param {string} props.titre nom du type
 * @param {string[]} props.mois mois choisis, « AAAA-MM »
 * @param {object} props.pieces pièces de la société (frais, chantiers, achatsJoints, factures, devis)
 * @param {object} [props.soc] entreprise
 * @returns {import('react').ReactElement} les choix d'envoi
 */
export function EnvoyerDuType({ type, titre, mois, pieces, soc }) {
  const [categorie, setCategorie] = useState(type);
  const types = categorie === CAFE_ET_REPAS ? TYPES_CAFE_ET_REPAS : [categorie];
  const garde = (t, x) => types.includes(t) && dansLaPeriode(dateIso(x.date), mois);
  const contenu = contenuDuDossier(pieces, 'tout', garde);
  const choisies = [...contenu.tickets, ...contenu.mensuelles, ...contenu.achats];
  const nom = categorie === CAFE_ET_REPAS ? tr('Café + Repas') : titre;
  const intitule = mois.length ? nom + ' · ' + libellePeriode(mois) : nom;
  const dossier = () =>
    demandeDuDossier(
      pieces,
      {
        annee: 'tout',
        resume: choisies.length > 1,
        registre: choisies.length > 1,
        tickets: true,
        mensuelles: true,
        achats: true,
        titre: intitule,
        garde,
      },
      soc,
    );

  return (
    <div data-testid="envoyer-type">
      {TYPES_CAFE_ET_REPAS.includes(type) && (
        <label style={{ display: 'block', marginBottom: 8 }}>
          <span style={styleEtiquette}>{tr('Catégorie')}</span>
          <select
            value={categorie}
            onChange={(e) => setCategorie(e.target.value)}
            style={styleChamp}
          >
            <option value={type}>{titre}</option>
            <option value={CAFE_ET_REPAS}>{tr('Café + Repas')}</option>
          </select>
        </label>
      )}
      <ActionsDossier nombre={choisies.length} titre={intitule} dossier={dossier} />
    </div>
  );
}

/**
 * Dans la Comptabilité, pour chaque type de dépense : « 📅 Voir / Envoyer
 * par mois ». On touche les mois, puis « 👁 Voir » ou « 📤 Envoyer ».
 *
 * @param {object} props propriétés
 * @param {string} props.type type de dépense
 * @param {string} props.titre nom du type
 * @param {object[]} props.items pièces du type
 * @param {object} props.pieces pièces de la société
 * @param {string|number} [props.annee] année affichée dans la Comptabilité
 * @param {object} [props.soc] entreprise
 * @returns {import('react').ReactElement} le bouton et, ouvert, le choix
 */
export function PanneauDuType({ type, titre, items, pieces, annee, soc }) {
  const [ouvert, setOuvert] = useState(false);
  const [mois, setMois] = useState(() => moisDeLAnnee(items, annee || anneeLaPlusRecente(items)));
  const [vue, setVue] = useState('');
  const retenues = (items || []).filter((it) => dansLaPeriode(dateIso(it.date), mois));
  const total = retenues.reduce((s, it) => s + (+it.montant || 0), 0);
  const gros = (quoi, texte, couleur) => (
    <button
      type="button"
      aria-pressed={vue === quoi}
      onClick={() => setVue((v) => (v === quoi ? '' : quoi))}
      style={{
        border: vue === quoi ? 'none' : '2px solid ' + couleur,
        background: vue === quoi ? couleur : '#fff',
        color: vue === quoi ? '#fff' : couleur,
        borderRadius: 12,
        padding: '13px 6px',
        fontSize: 14,
        fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      {texte}
    </button>
  );
  return (
    <div style={{ margin: '4px 0 8px' }}>
      <Btn
        label={'📅 ' + tr('Voir / Envoyer par mois')}
        color={C.teal}
        ghost={!ouvert}
        sm
        full
        onClick={() => setOuvert((o) => !o)}
      />
      {ouvert && (
        <div data-testid="panneau-du-type" style={{ ...styleCadre, marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <b style={{ fontSize: 14, color: C.navy }}>{titre}</b>
            <Btn label={tr('Fermer')} ghost color={C.grey} sm onClick={() => setOuvert(false)} />
          </div>
          <ChoixDesMois elements={items} mois={mois} onChange={setMois} annee={annee} />
          <ResumeDesMois mois={mois} pieces={retenues.length} total={total} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 8,
              marginBottom: vue ? 10 : 0,
            }}
          >
            {gros('voir', '👁 ' + tr('Voir'), C.teal)}
            {gros('envoyer', '📤 ' + tr('Envoyer'), C.green)}
          </div>
          {vue === 'voir' && <ConsulterDuType items={items} mois={mois} soc={soc} />}
          {vue === 'envoyer' && (
            <EnvoyerDuType type={type} titre={titre} mois={mois} pieces={pieces} soc={soc} />
          )}
        </div>
      )}
    </div>
  );
}
